import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  CheckCircle2, 
  Download,
  ArrowRight,
  ArrowLeft,
  Calculator,
  DollarSign,
  CreditCard,
  Utensils,
  Gift,
  Plane,
  Sparkles,
  Target,
  BookOpen,
  AlertTriangle,
  TrendingUp,
  Scale,
  HelpCircle,
  PiggyBank,
  FileText,
  Lock
} from "lucide-react";
import { toast } from "sonner";

const WorkbookPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [hasAccess, setHasAccess] = useState(false);
  
  // Check if user has purchased an ebook or course
  useEffect(() => {
    const checkAccess = () => {
      // Check localStorage for purchase or training registration
      const hasPurchased = localStorage.getItem('ebookPurchased');
      const hasRegistered = localStorage.getItem('freeTrainingRegistered');
      if (hasPurchased || hasRegistered) {
        setHasAccess(true);
      }
    };
    checkAccess();
  }, []);

  // Workbook data state
  const [data, setData] = useState({
    // Part 1: Debts
    debts: [
      { creditor: "", type: "", balance: "", minPayment: "", rate: "" },
      { creditor: "", type: "", balance: "", minPayment: "", rate: "" },
      { creditor: "", type: "", balance: "", minPayment: "", rate: "" },
      { creditor: "", type: "", balance: "", minPayment: "", rate: "" },
      { creditor: "", type: "", balance: "", minPayment: "", rate: "" },
    ],
    // Income
    income: {
      primaryJob: "",
      secondJob: "",
      childSupport: "",
      benefits: "",
      other: ""
    },
    // Part 2: Spending
    subscriptions: [
      { name: "", cost: "", useIt: true, keep: true },
      { name: "", cost: "", useIt: true, keep: true },
      { name: "", cost: "", useIt: true, keep: true },
      { name: "", cost: "", useIt: true, keep: true },
      { name: "", cost: "", useIt: true, keep: true },
    ],
    eatingOut: {
      coffee: { times: "", avgCost: "" },
      fastFood: { times: "", avgCost: "" },
      casual: { times: "", avgCost: "" },
      fine: { times: "", avgCost: "" },
      delivery: { times: "", avgCost: "" },
      snacks: { times: "", avgCost: "" }
    },
    gifts: {
      christmas: "",
      birthdays: "",
      mothersDay: "",
      valentines: "",
      anniversaries: "",
      justBecause: "",
      kidsEvents: "",
      other: ""
    },
    travel: "",
    entertainment: {
      kidsSports: "",
      hobbies: "",
      concerts: "",
      movies: "",
      gaming: "",
      beauty: "",
      other: ""
    },
    // Part 3: Savings potential
    potentialSavings: {
      subscriptions: "",
      eatingOut: "",
      gifts: "",
      travel: "",
      entertainment: ""
    },
    // Part 4: Decision
    decision: "",
    nextStep: "",
    commitmentDate: ""
  });

  // Calculate totals
  const totalDebt = data.debts.reduce((sum, d) => sum + (parseFloat(d.balance) || 0), 0);
  const totalMinPayments = data.debts.reduce((sum, d) => sum + (parseFloat(d.minPayment) || 0), 0);
  const totalIncome = Object.values(data.income).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  const debtRatio = totalIncome > 0 ? ((totalMinPayments / totalIncome) * 100).toFixed(1) : 0;
  
  const totalSubscriptions = data.subscriptions.reduce((sum, s) => sum + (parseFloat(s.cost) || 0), 0);
  const subscriptionsToCancel = data.subscriptions
    .filter(s => !s.keep)
    .reduce((sum, s) => sum + (parseFloat(s.cost) || 0), 0);

  const totalEatingOut = Object.values(data.eatingOut).reduce((sum, item) => {
    return sum + ((parseFloat(item.times) || 0) * (parseFloat(item.avgCost) || 0));
  }, 0);

  const totalGifts = Object.values(data.gifts).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  const monthlyGifts = totalGifts / 12;

  const totalEntertainment = Object.values(data.entertainment).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);

  const totalLifestyleSpending = totalSubscriptions + totalEatingOut + monthlyGifts + (parseFloat(data.travel) || 0) / 12 + totalEntertainment;

  const totalPotentialSavings = Object.values(data.potentialSavings).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);

  const getDebtRatioMessage = () => {
    const ratio = parseFloat(debtRatio);
    if (ratio < 20) return { level: "Manageable", color: "text-green-600", bg: "bg-green-100", message: "Better budgeting may be enough" };
    if (ratio < 36) return { level: "Concerning", color: "text-yellow-600", bg: "bg-yellow-100", message: "Consolidation or counseling could help" };
    if (ratio <= 50) return { level: "Serious", color: "text-orange-600", bg: "bg-orange-100", message: "Professional intervention recommended" };
    return { level: "Critical", color: "text-red-600", bg: "bg-red-100", message: "All options should be on the table" };
  };

  const steps = [
    { title: "Your Debts", icon: CreditCard },
    { title: "Your Income", icon: DollarSign },
    { title: "Subscriptions", icon: Sparkles },
    { title: "Eating Out", icon: Utensils },
    { title: "Gift Spending", icon: Gift },
    { title: "Travel & Fun", icon: Plane },
    { title: "Your Summary", icon: Calculator },
    { title: "Your Path", icon: Target },
    { title: "Action Plan", icon: CheckCircle2 }
  ];

  const handleDownload = () => {
    window.open('/financial-clarity-workbook.md', '_blank');
    toast.success("Workbook downloaded!");
  };

  // If no access, show gated view
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1E3A5F] via-[#162c47] to-[#0f1e32] flex items-center justify-center px-6">
        <div className="max-w-lg text-center">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
            <Lock className="h-16 w-16 text-[#9D9045] mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              The Financial Clarity Workbook
            </h1>
            <p className="text-gray-300 mb-6">
              This interactive workbook is included as a bonus with any ebook or course purchase, or when you register for our free training.
            </p>
            <div className="space-y-4">
              <Button 
                onClick={() => navigate('/training')}
                size="lg"
                className="w-full bg-[#9D9045] hover:bg-[#8a7e3c] text-white"
              >
                Watch Free Training (Get Access)
              </Button>
              <Button 
                onClick={() => navigate('/ebooks')}
                variant="outline"
                size="lg"
                className="w-full border-white/30 text-white hover:bg-white/10"
              >
                Browse Ebooks
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="workbook-page">
      {/* Header */}
      <div className="bg-[#1E3A5F] py-8">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                The Financial Clarity Workbook
              </h1>
              <p className="text-gray-300">Your Personal Action Plan</p>
            </div>
            <Button 
              onClick={handleDownload}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10"
            >
              <Download className="mr-2 h-4 w-4" />
              Download PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between overflow-x-auto">
            {steps.map((step, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={`flex flex-col items-center min-w-[80px] px-2 ${
                  currentStep === index 
                    ? "text-[#9D9045]" 
                    : currentStep > index 
                      ? "text-green-600" 
                      : "text-gray-400"
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 ${
                  currentStep === index 
                    ? "bg-[#9D9045] text-white" 
                    : currentStep > index 
                      ? "bg-green-100 text-green-600" 
                      : "bg-gray-100"
                }`}>
                  {currentStep > index ? (
                    <CheckCircle2 className="h-5 w-5" />
                  ) : (
                    <step.icon className="h-5 w-5" />
                  )}
                </div>
                <span className="text-xs font-medium whitespace-nowrap">{step.title}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Step 0: Debts */}
        {currentStep === 0 && (
          <div className="bg-white rounded-xl shadow-sm p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-[#1E3A5F] w-12 h-12 rounded-xl flex items-center justify-center">
                <CreditCard className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#1E3A5F]">Step 1: List All Your Debts</h2>
                <p className="text-gray-500">Don't skip anything—even small amounts</p>
              </div>
            </div>

            <div className="space-y-4">
              {data.debts.map((debt, index) => (
                <div key={index} className="grid grid-cols-5 gap-3">
                  <Input
                    placeholder="Creditor (e.g., Chase)"
                    value={debt.creditor}
                    onChange={(e) => {
                      const newDebts = [...data.debts];
                      newDebts[index].creditor = e.target.value;
                      setData({ ...data, debts: newDebts });
                    }}
                  />
                  <Input
                    placeholder="Type"
                    value={debt.type}
                    onChange={(e) => {
                      const newDebts = [...data.debts];
                      newDebts[index].type = e.target.value;
                      setData({ ...data, debts: newDebts });
                    }}
                  />
                  <Input
                    placeholder="Balance"
                    type="number"
                    value={debt.balance}
                    onChange={(e) => {
                      const newDebts = [...data.debts];
                      newDebts[index].balance = e.target.value;
                      setData({ ...data, debts: newDebts });
                    }}
                  />
                  <Input
                    placeholder="Min Payment"
                    type="number"
                    value={debt.minPayment}
                    onChange={(e) => {
                      const newDebts = [...data.debts];
                      newDebts[index].minPayment = e.target.value;
                      setData({ ...data, debts: newDebts });
                    }}
                  />
                  <Input
                    placeholder="Rate %"
                    type="number"
                    value={debt.rate}
                    onChange={(e) => {
                      const newDebts = [...data.debts];
                      newDebts[index].rate = e.target.value;
                      setData({ ...data, debts: newDebts });
                    }}
                  />
                </div>
              ))}
              <Button
                variant="outline"
                onClick={() => setData({ 
                  ...data, 
                  debts: [...data.debts, { creditor: "", type: "", balance: "", minPayment: "", rate: "" }]
                })}
                className="w-full"
              >
                + Add Another Debt
              </Button>
            </div>

            <div className="mt-8 p-6 bg-gray-50 rounded-xl">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-500">Total Debt</p>
                  <p className="text-3xl font-bold text-[#1E3A5F]">${totalDebt.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Minimum Payments</p>
                  <p className="text-3xl font-bold text-[#9D9045]">${totalMinPayments.toLocaleString()}/mo</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 1: Income */}
        {currentStep === 1 && (
          <div className="bg-white rounded-xl shadow-sm p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-[#1E3A5F] w-12 h-12 rounded-xl flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#1E3A5F]">Step 2: Your Monthly Income</h2>
                <p className="text-gray-500">After taxes</p>
              </div>
            </div>

            <div className="space-y-4">
              {[
                { key: "primaryJob", label: "Primary Job" },
                { key: "secondJob", label: "Second Job / Side Hustle" },
                { key: "childSupport", label: "Child Support / Alimony" },
                { key: "benefits", label: "Government Benefits" },
                { key: "other", label: "Other Income" }
              ].map((item) => (
                <div key={item.key} className="flex items-center gap-4">
                  <label className="w-48 text-gray-700">{item.label}</label>
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <Input
                      type="number"
                      placeholder="0"
                      className="pl-8"
                      value={data.income[item.key]}
                      onChange={(e) => setData({ 
                        ...data, 
                        income: { ...data.income, [item.key]: e.target.value }
                      })}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 p-6 bg-gray-50 rounded-xl">
              <p className="text-sm text-gray-500">Total Monthly Income</p>
              <p className="text-3xl font-bold text-green-600">${totalIncome.toLocaleString()}/mo</p>
            </div>

            {/* Debt Ratio Calculator */}
            {totalIncome > 0 && totalMinPayments > 0 && (
              <div className="mt-6 p-6 bg-[#1E3A5F] rounded-xl text-white">
                <h3 className="font-bold mb-4">Your Debt-to-Income Ratio</h3>
                <div className="flex items-center gap-4 mb-4">
                  <span className="text-4xl font-bold">{debtRatio}%</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-bold ${getDebtRatioMessage().bg} ${getDebtRatioMessage().color}`}>
                    {getDebtRatioMessage().level}
                  </span>
                </div>
                <p className="text-gray-300">{getDebtRatioMessage().message}</p>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Subscriptions */}
        {currentStep === 2 && (
          <div className="bg-white rounded-xl shadow-sm p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-[#1E3A5F] w-12 h-12 rounded-xl flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#1E3A5F]">Step 3: Subscription Audit</h2>
                <p className="text-gray-500">List every recurring subscription</p>
              </div>
            </div>

            <div className="space-y-4">
              {data.subscriptions.map((sub, index) => (
                <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <Input
                    placeholder="Subscription name"
                    className="flex-1"
                    value={sub.name}
                    onChange={(e) => {
                      const newSubs = [...data.subscriptions];
                      newSubs[index].name = e.target.value;
                      setData({ ...data, subscriptions: newSubs });
                    }}
                  />
                  <div className="relative w-32">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <Input
                      type="number"
                      placeholder="Cost"
                      className="pl-8"
                      value={sub.cost}
                      onChange={(e) => {
                        const newSubs = [...data.subscriptions];
                        newSubs[index].cost = e.target.value;
                        setData({ ...data, subscriptions: newSubs });
                      }}
                    />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sub.keep}
                      onChange={(e) => {
                        const newSubs = [...data.subscriptions];
                        newSubs[index].keep = e.target.checked;
                        setData({ ...data, subscriptions: newSubs });
                      }}
                      className="w-5 h-5 rounded border-gray-300"
                    />
                    <span className="text-sm">Keep</span>
                  </label>
                </div>
              ))}
              <Button
                variant="outline"
                onClick={() => setData({ 
                  ...data, 
                  subscriptions: [...data.subscriptions, { name: "", cost: "", useIt: true, keep: true }]
                })}
                className="w-full"
              >
                + Add Another
              </Button>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-6">
              <div className="p-6 bg-gray-50 rounded-xl">
                <p className="text-sm text-gray-500">Total Subscriptions</p>
                <p className="text-3xl font-bold text-[#1E3A5F]">${totalSubscriptions.toFixed(2)}/mo</p>
              </div>
              <div className="p-6 bg-red-50 rounded-xl">
                <p className="text-sm text-red-600">Could Cancel</p>
                <p className="text-3xl font-bold text-red-600">${subscriptionsToCancel.toFixed(2)}/mo</p>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Eating Out */}
        {currentStep === 3 && (
          <div className="bg-white rounded-xl shadow-sm p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-[#1E3A5F] w-12 h-12 rounded-xl flex items-center justify-center">
                <Utensils className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#1E3A5F]">Step 4: Eating Out Tracker</h2>
                <p className="text-gray-500">Last 30 days of non-grocery food purchases</p>
              </div>
            </div>

            <div className="space-y-4">
              {[
                { key: "coffee", label: "Coffee Shops" },
                { key: "fastFood", label: "Fast Food" },
                { key: "casual", label: "Casual Dining" },
                { key: "fine", label: "Fine Dining" },
                { key: "delivery", label: "Food Delivery (DoorDash, etc.)" },
                { key: "snacks", label: "Snacks / Convenience" }
              ].map((item) => (
                <div key={item.key} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <span className="w-48 text-gray-700">{item.label}</span>
                  <Input
                    type="number"
                    placeholder="# times"
                    className="w-24"
                    value={data.eatingOut[item.key].times}
                    onChange={(e) => setData({
                      ...data,
                      eatingOut: {
                        ...data.eatingOut,
                        [item.key]: { ...data.eatingOut[item.key], times: e.target.value }
                      }
                    })}
                  />
                  <span className="text-gray-400">×</span>
                  <div className="relative w-24">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <Input
                      type="number"
                      placeholder="Avg"
                      className="pl-8"
                      value={data.eatingOut[item.key].avgCost}
                      onChange={(e) => setData({
                        ...data,
                        eatingOut: {
                          ...data.eatingOut,
                          [item.key]: { ...data.eatingOut[item.key], avgCost: e.target.value }
                        }
                      })}
                    />
                  </div>
                  <span className="text-gray-400">=</span>
                  <span className="font-bold text-[#1E3A5F] w-20">
                    ${((parseFloat(data.eatingOut[item.key].times) || 0) * (parseFloat(data.eatingOut[item.key].avgCost) || 0)).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-8 p-6 bg-[#9D9045]/10 rounded-xl">
              <p className="text-sm text-[#9D9045]">Total Eating Out</p>
              <p className="text-3xl font-bold text-[#9D9045]">${totalEatingOut.toFixed(2)}/mo</p>
              <p className="text-sm text-gray-600 mt-2">
                That's ${(totalEatingOut * 12).toLocaleString()}/year!
              </p>
            </div>
          </div>
        )}

        {/* Step 4: Gifts */}
        {currentStep === 4 && (
          <div className="bg-white rounded-xl shadow-sm p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-[#1E3A5F] w-12 h-12 rounded-xl flex items-center justify-center">
                <Gift className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#1E3A5F]">Step 5: Gift Giving Audit</h2>
                <p className="text-gray-500">Annual spending on gifts</p>
              </div>
            </div>

            <div className="space-y-4">
              {[
                { key: "christmas", label: "Christmas / Holiday Gifts" },
                { key: "birthdays", label: "Birthday Gifts" },
                { key: "mothersDay", label: "Mother's / Father's Day" },
                { key: "valentines", label: "Valentine's Day" },
                { key: "anniversaries", label: "Anniversaries" },
                { key: "justBecause", label: "\"Just Because\" Gifts" },
                { key: "kidsEvents", label: "Kids' Events (parties, etc.)" },
                { key: "other", label: "Other" }
              ].map((item) => (
                <div key={item.key} className="flex items-center gap-4">
                  <span className="w-48 text-gray-700">{item.label}</span>
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <Input
                      type="number"
                      placeholder="0"
                      className="pl-8"
                      value={data.gifts[item.key]}
                      onChange={(e) => setData({
                        ...data,
                        gifts: { ...data.gifts, [item.key]: e.target.value }
                      })}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 grid grid-cols-2 gap-6">
              <div className="p-6 bg-gray-50 rounded-xl">
                <p className="text-sm text-gray-500">Annual Gift Spending</p>
                <p className="text-3xl font-bold text-[#1E3A5F]">${totalGifts.toLocaleString()}/yr</p>
              </div>
              <div className="p-6 bg-[#9D9045]/10 rounded-xl">
                <p className="text-sm text-[#9D9045]">Monthly Average</p>
                <p className="text-3xl font-bold text-[#9D9045]">${monthlyGifts.toFixed(2)}/mo</p>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Travel & Entertainment */}
        {currentStep === 5 && (
          <div className="bg-white rounded-xl shadow-sm p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-[#1E3A5F] w-12 h-12 rounded-xl flex items-center justify-center">
                <Plane className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#1E3A5F]">Step 6: Travel & Entertainment</h2>
                <p className="text-gray-500">Fun stuff that adds up</p>
              </div>
            </div>

            <div className="mb-8">
              <label className="block text-gray-700 font-medium mb-2">Annual Travel / Vacation Spending</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <Input
                  type="number"
                  placeholder="0"
                  className="pl-8"
                  value={data.travel}
                  onChange={(e) => setData({ ...data, travel: e.target.value })}
                />
              </div>
            </div>

            <h3 className="font-bold text-[#1E3A5F] mb-4">Monthly Entertainment & Activities</h3>
            <div className="space-y-4">
              {[
                { key: "kidsSports", label: "Kids' Sports / Activities" },
                { key: "hobbies", label: "Your Hobbies" },
                { key: "concerts", label: "Concerts / Events" },
                { key: "movies", label: "Movies / Entertainment" },
                { key: "gaming", label: "Gaming / Apps" },
                { key: "beauty", label: "Beauty / Spa Services" },
                { key: "other", label: "Other" }
              ].map((item) => (
                <div key={item.key} className="flex items-center gap-4">
                  <span className="w-48 text-gray-700">{item.label}</span>
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <Input
                      type="number"
                      placeholder="0"
                      className="pl-8"
                      value={data.entertainment[item.key]}
                      onChange={(e) => setData({
                        ...data,
                        entertainment: { ...data.entertainment, [item.key]: e.target.value }
                      })}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 p-6 bg-gray-50 rounded-xl">
              <p className="text-sm text-gray-500">Total Entertainment</p>
              <p className="text-3xl font-bold text-[#1E3A5F]">${totalEntertainment.toFixed(2)}/mo</p>
            </div>
          </div>
        )}

        {/* Step 6: Summary */}
        {currentStep === 6 && (
          <div className="bg-white rounded-xl shadow-sm p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-[#1E3A5F] w-12 h-12 rounded-xl flex items-center justify-center">
                <Calculator className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#1E3A5F]">Your Financial Summary</h2>
                <p className="text-gray-500">The numbers don't lie</p>
              </div>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="p-4 bg-red-50 rounded-xl text-center">
                <p className="text-sm text-red-600">Total Debt</p>
                <p className="text-2xl font-bold text-red-600">${totalDebt.toLocaleString()}</p>
              </div>
              <div className="p-4 bg-green-50 rounded-xl text-center">
                <p className="text-sm text-green-600">Monthly Income</p>
                <p className="text-2xl font-bold text-green-600">${totalIncome.toLocaleString()}</p>
              </div>
              <div className="p-4 bg-orange-50 rounded-xl text-center">
                <p className="text-sm text-orange-600">Min. Payments</p>
                <p className="text-2xl font-bold text-orange-600">${totalMinPayments.toLocaleString()}/mo</p>
              </div>
              <div className={`p-4 rounded-xl text-center ${getDebtRatioMessage().bg}`}>
                <p className={`text-sm ${getDebtRatioMessage().color}`}>Debt Ratio</p>
                <p className={`text-2xl font-bold ${getDebtRatioMessage().color}`}>{debtRatio}%</p>
              </div>
            </div>

            {/* Lifestyle Spending Breakdown */}
            <h3 className="font-bold text-[#1E3A5F] mb-4">Your "Lifestyle" Spending</h3>
            <div className="space-y-3 mb-8">
              {[
                { label: "Subscriptions", value: totalSubscriptions },
                { label: "Eating Out", value: totalEatingOut },
                { label: "Gifts (monthly avg)", value: monthlyGifts },
                { label: "Travel (monthly avg)", value: (parseFloat(data.travel) || 0) / 12 },
                { label: "Entertainment", value: totalEntertainment }
              ].map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-700">{item.label}</span>
                  <span className="font-bold text-[#1E3A5F]">${item.value.toFixed(2)}/mo</span>
                </div>
              ))}
              <div className="flex items-center justify-between p-4 bg-[#9D9045]/10 rounded-lg">
                <span className="font-bold text-[#9D9045]">TOTAL LIFESTYLE SPENDING</span>
                <span className="text-xl font-bold text-[#9D9045]">${totalLifestyleSpending.toFixed(2)}/mo</span>
              </div>
            </div>

            {/* Potential Savings */}
            <h3 className="font-bold text-[#1E3A5F] mb-4">How Much Could You Save?</h3>
            <div className="space-y-4">
              {[
                { key: "subscriptions", label: "Cancel subscriptions" },
                { key: "eatingOut", label: "Reduce eating out" },
                { key: "gifts", label: "Reduce gift spending" },
                { key: "travel", label: "Reduce travel" },
                { key: "entertainment", label: "Reduce entertainment" }
              ].map((item) => (
                <div key={item.key} className="flex items-center gap-4">
                  <span className="w-48 text-gray-700">{item.label}</span>
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <Input
                      type="number"
                      placeholder="0"
                      className="pl-8"
                      value={data.potentialSavings[item.key]}
                      onChange={(e) => setData({
                        ...data,
                        potentialSavings: { ...data.potentialSavings, [item.key]: e.target.value }
                      })}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 p-6 bg-green-50 rounded-xl">
              <p className="text-sm text-green-600">Total Potential Monthly Savings</p>
              <p className="text-3xl font-bold text-green-600">${totalPotentialSavings.toFixed(2)}/mo</p>
              <p className="text-sm text-gray-600 mt-2">
                {totalPotentialSavings >= totalMinPayments 
                  ? "✅ This exceeds your minimum payments - budgeting may work!" 
                  : "⚠️ This is less than your minimums - you may need additional help"}
              </p>
            </div>
          </div>
        )}

        {/* Step 7: Your Path */}
        {currentStep === 7 && (
          <div className="bg-white rounded-xl shadow-sm p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-[#1E3A5F] w-12 h-12 rounded-xl flex items-center justify-center">
                <Target className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#1E3A5F]">Choose Your Path Forward</h2>
                <p className="text-gray-500">Based on your numbers</p>
              </div>
            </div>

            <div className="space-y-4">
              {[
                {
                  id: "budgeting",
                  icon: PiggyBank,
                  title: "Better Budgeting",
                  desc: "DIY approach with discipline",
                  best: "Debt ratio under 35%, can pay off in under 3 years",
                  color: "green"
                },
                {
                  id: "consolidation",
                  icon: CreditCard,
                  title: "Debt Consolidation",
                  desc: "Combine debts into one lower payment",
                  best: "Credit score 640+, multiple high-interest debts",
                  color: "blue"
                },
                {
                  id: "counseling",
                  icon: HelpCircle,
                  title: "Credit Counseling",
                  desc: "Professional help negotiating with creditors",
                  best: "Debt ratio 35-50%, need structure & accountability",
                  color: "purple"
                },
                {
                  id: "bankruptcy",
                  icon: Scale,
                  title: "Bankruptcy",
                  desc: "Legal fresh start - eliminate qualifying debts",
                  best: "Debt ratio over 50%, being sued/garnished, need fastest relief",
                  color: "orange"
                }
              ].map((option) => (
                <button
                  key={option.id}
                  onClick={() => setData({ ...data, decision: option.id })}
                  className={`w-full p-6 rounded-xl border-2 text-left transition-all ${
                    data.decision === option.id 
                      ? `border-${option.color}-500 bg-${option.color}-50` 
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      data.decision === option.id ? `bg-${option.color}-500 text-white` : "bg-gray-100"
                    }`}>
                      <option.icon className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-bold text-[#1E3A5F]">{option.title}</h3>
                        {data.decision === option.id && (
                          <CheckCircle2 className="h-5 w-5 text-green-500" />
                        )}
                      </div>
                      <p className="text-gray-600">{option.desc}</p>
                      <p className="text-sm text-gray-500 mt-2">Best for: {option.best}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 8: Action Plan */}
        {currentStep === 8 && (
          <div className="bg-white rounded-xl shadow-sm p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-green-500 w-12 h-12 rounded-xl flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#1E3A5F]">Your Action Plan</h2>
                <p className="text-gray-500">Commit to your next step</p>
              </div>
            </div>

            <div className="bg-[#1E3A5F] rounded-xl p-6 text-white mb-8">
              <p className="text-sm text-gray-300 mb-2">Based on your analysis, you chose:</p>
              <p className="text-2xl font-bold">
                {data.decision === "budgeting" && "Better Budgeting"}
                {data.decision === "consolidation" && "Debt Consolidation"}
                {data.decision === "counseling" && "Credit Counseling"}
                {data.decision === "bankruptcy" && "Bankruptcy"}
                {!data.decision && "No option selected yet"}
              </p>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  My next step will be:
                </label>
                <Input
                  placeholder="e.g., Call a credit counselor, cancel Netflix, meet with attorney..."
                  value={data.nextStep}
                  onChange={(e) => setData({ ...data, nextStep: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  I will take this step by:
                </label>
                <Input
                  type="date"
                  value={data.commitmentDate}
                  onChange={(e) => setData({ ...data, commitmentDate: e.target.value })}
                />
              </div>
            </div>

            <div className="mt-8 p-6 bg-gray-50 rounded-xl">
              <h3 className="font-bold text-[#1E3A5F] mb-4">Immediate Actions This Week:</h3>
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" className="w-5 h-5 rounded" />
                  <span>Cancel unnecessary subscriptions</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" className="w-5 h-5 rounded" />
                  <span>Set a weekly food budget</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" className="w-5 h-5 rounded" />
                  <span>Stop using credit cards</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" className="w-5 h-5 rounded" />
                  <span>Create a written budget</span>
                </label>
              </div>
            </div>

            {/* CTAs */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button 
                onClick={handleDownload}
                size="lg"
                variant="outline"
                className="w-full"
              >
                <Download className="mr-2 h-5 w-5" />
                Download PDF Workbook
              </Button>
              <Button 
                onClick={() => navigate('/services')}
                size="lg"
                className="w-full bg-[#9D9045] hover:bg-[#8a7e3c] text-white"
              >
                Book a Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <Button
            variant="outline"
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
            disabled={currentStep === 0}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Previous
          </Button>
          <Button
            onClick={() => setCurrentStep(Math.min(steps.length - 1, currentStep + 1))}
            disabled={currentStep === steps.length - 1}
            className="bg-[#1E3A5F] hover:bg-[#2d4a6f] text-white"
          >
            Next
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WorkbookPage;
